import { observer } from "mobx-react-lite";
import { useEffect} from "react";
import { PubSubEvents } from "../../services/PubSubEvents";
import React from "react";
import { InspectionTable } from "./InspectionTable";
import { inspectionInfoStorage } from "../../services/ocr/InspectionInfoStorageModel";
import { Grid } from "@mui/material";
import ReportLoader from "./ReportLoader";
import { DetectionList } from "./DetectionList";
import { OcrService } from "../../services/ocr/OcrService";
import { SegmentationService } from "../../services/segmentation/SegmentationService";
import { Segmentation } from "../../api-models/response/VideoAnalysisDataResponse";
import { ApprovedList } from "./useApprovedList";


// Left-hand report content for the Inspection Insights tab: the inspection table
// (with its pipe + arrows) and the detection list. Main Information, the video
// player and the per-detection insight panel are now rendered at the page level
// (shared/sticky across all tabs), so they no longer live here.
export interface IInspectionReport {
    fileId: number,
    onFinished(fileId: number): void,
    ocr: OcrService,
    // "Mark damage" option: when on, detection frames get segmentation polygons.
    segmentationEnabled?: boolean,
    segmentation?: SegmentationService,
    filteredSegmentations?: Segmentation[],
    // The OCR side's approved list. Every detection edit made in this report goes through it,
    // from either view — see DetectionList's prop for why nothing writes to ocr.json.
    approved?: ApprovedList,
    // True while the rows shown are the raw ocr.json output rather than the approved list.
    showingMachineOutput?: boolean,
    // The station at a video position, from the analysis run's distance track (if any).
    distanceForTime?: (seconds: number) => number | undefined,
}

export default observer(function InspectionReport(props: IInspectionReport) {
  const { fileId, onFinished, ocr } = props;

  useEffect(() => {
    if (fileId === undefined) return;
    var segmentationSubscribeToken = PubSub.subscribe(
      PubSubEvents.VideoOcrFinished,
      async (_event: string, notification: Notification) => {
        onFinished(notification.data.fileId);
      }
    );

    return function cleanup() {
      PubSub.unsubscribe(segmentationSubscribeToken);
    };
  }, [onFinished, fileId, ocr.status]);

  useEffect(() => {
    if (!ocr.ocrRunning) return;
    const interval = window.setInterval(() => {
      void (ocr as any).checkStatus();
    }, 10000);
    return () => window.clearInterval(interval);
  }, [ocr.ocrRunning, ocr]);

  return (
    <Grid container spacing={2}>
      <ReportLoader ocr={ocr}>
        <Grid item xs={12}>
          <Grid container direction="column" spacing={2}>
            {/* Always rendered, empty included: with nothing approved the pipe is drawn bare —
                the section with no damages on it — rather than disappearing. What must NOT
                appear is per-detection content: no arrows on the pipe, no rows in the grid and
                no frame cards below, because nothing has been approved yet. */}
            <Grid
              item
              xs={12}
              sx={{ width: { xs: "auto", lg: "100%" }, marginLeft: { xs: -8, lg: 0 } }}
              id="pdf-content-table"
            >
              <InspectionTable
                inspectionInsightStorage={inspectionInfoStorage}
                ocrService={props.ocr}
                reviewed={props.showingMachineOutput === false}
              />
            </Grid>
            <Grid item xs={12} sx={{ width: "100%" }}>
              <DetectionList
                inspectionInsightStorage={inspectionInfoStorage}
                ocrService={ocr}
                segmentationEnabled={props.segmentationEnabled}
                segmentation={props.segmentation}
                filteredSegmentations={props.filteredSegmentations}
                approved={props.approved}
                showingMachineOutput={props.showingMachineOutput}
                distanceForTime={props.distanceForTime}
              />
            </Grid>
          </Grid>
        </Grid>
      </ReportLoader>
    </Grid>
  );
});
